<script setup lang="ts">
import { computed, ref } from 'vue'
import { useSelectionStore } from '@/stores/selection'
import {
  useMallCategoryMapping,
  useManageBrowseNodeMutation,
  useSaveMallCategoryMutation,
  useOfferDetail,
} from '@/api/queries'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useResultModalStore } from '@/stores/resultModal'
import type { MallCategoryEntry, MallCategoryBrowseNode } from '@/types/api'
import BrowseNodePickerModal from './BrowseNodePickerModal.vue'
import MallCategoryPickerModal from './MallCategoryPickerModal.vue'

const selection = useSelectionStore()
const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()
const asinRef = computed(() => selection.selectedAsin)
const sellerIdRef = computed(() => selection.selectedSellerId)

const { data, isLoading } = useMallCategoryMapping(asinRef)
const { data: detail } = useOfferDetail(asinRef, sellerIdRef)
const manageMutation = useManageBrowseNodeMutation()
const saveCategoryMutation = useSaveMallCategoryMutation()

const browseNodes = computed(() => data.value?.browseNodes ?? [])
// VB.NET版: ブラウズノードが0件の場合、パネル背景を危険色(赤)にする
const hasBrowseNode = computed(() => data.value?.hasBrowseNode !== false)
const productName = computed(() => detail.value?.product?.Title ?? '')
const assignedIds = computed(() => browseNodes.value.map((n) => n.browseNodeId))

// 表示専用モードは廃止し、常に編集モード相当のUI(ブラウズノードの追加/削除/兄弟反映)を表示する
const showPicker = ref(false)


// --- ブラウズノード削除 ---
async function deleteNode(browseNodeId: string) {
  const asin = selection.selectedAsin
  if (!asin) return
  if (!(await confirmModal.ask(`ブラウズノード「${browseNodeId}」を削除しますか？\n（商品は「要再確認」状態になります）`))) return
  try {
    await manageMutation.mutateAsync({ asin, req: { action: 'delete', browseNodeId } })
    resultModal.show('ブラウズノードを削除しました。')
  } catch {
    resultModal.show('削除に失敗しました。', 'ng')
  }
}

// --- ブラウズノード追加(モーダルで選択) ---
async function onSelectBrowseNode(browseNodeId: string) {
  const asin = selection.selectedAsin
  if (!asin) return
  showPicker.value = false
  try {
    await manageMutation.mutateAsync({ asin, req: { action: 'add', browseNodeId } })
    resultModal.show('ブラウズノードを追加しました。')
  } catch {
    resultModal.show('追加に失敗しました。', 'ng')
  }
}

// --- 兄弟へ反映 ---
async function shareToSiblings() {
  const asin = selection.selectedAsin
  if (!asin) return
  if (!(await confirmModal.ask('現在のブラウズノード構成を、同じ親を持つ兄弟商品すべてに反映しますか？'))) return
  try {
    const res = await manageMutation.mutateAsync({ asin, req: { action: 'share' } })
    resultModal.show(`兄弟商品 ${res.appliedSiblings ?? 0} 件に反映しました。`)
  } catch {
    resultModal.show('兄弟への反映に失敗しました。', 'ng')
  }
}

// 楽天のショップ別上書きのツールチップ文字列(VB版と同じ「ShopId - ShopName - CategoryName」)
function fixedItemTooltip(entry: MallCategoryEntry): string {
  if (!entry.fixedItem) return ''
  return entry.fixedItem.tooltip
    .map((t) => `${t.shopId} - ${t.shopName} - ${t.categoryName}`)
    .join('\n')
}

// ---- モールカテゴリ編集 (CategoryMapper2 / CategoryMapper3 相当) ----
// 編集対象を1つ保持する。scope で CM2(ブラウズノード単位) / CM3(ASIN単位) を切り替える。
interface CategoryEditTarget {
  scope: 'browsenode' | 'asin'
  mallName: string
  dbName: string
  targetLabel: string // ブラウズノードID or ASIN
  browseNodeId?: string
  currentCategoryId?: string
  currentEnableFlg?: string
  deletable?: boolean
}
const categoryEditTarget = ref<CategoryEditTarget | null>(null)

// CM2: ブラウズノード×モール のカテゴリを編集
function editBrowsenodeCategory(node: MallCategoryBrowseNode, mall: MallCategoryEntry) {
  categoryEditTarget.value = {
    scope: 'browsenode',
    mallName: mall.mallName,
    dbName: mall.dbName,
    targetLabel: node.browseNodeId,
    browseNodeId: node.browseNodeId,
    currentCategoryId: mall.categoryId || undefined,
    currentEnableFlg: mall.enabled ? '1' : '0',
    deletable: false,
  }
}

// CM3: ASIN×モール のカテゴリを編集(ASIN単位の上書き)
function editAsinCategory(mall: MallCategoryEntry) {
  categoryEditTarget.value = {
    scope: 'asin',
    mallName: mall.mallName,
    dbName: mall.dbName,
    targetLabel: selection.selectedAsin ?? '',
    currentCategoryId: mall.source === 'asin_override' ? mall.categoryId || undefined : undefined,
    currentEnableFlg: mall.enabled ? '1' : '0',
    deletable: mall.source === 'asin_override', // 既に上書きがある場合のみ削除可
  }
}

async function onSaveCategory(payload: { categoryId: string; categoryName: string; enableFlg: '0' | '1' }) {
  const t = categoryEditTarget.value
  const asin = selection.selectedAsin
  if (!t || !asin) return
  try {
    await saveCategoryMutation.mutateAsync({
      asin,
      req: {
        scope: t.scope,
        dbName: t.dbName,
        action: 'save',
        browseNodeId: t.browseNodeId,
        productCategoryId: payload.categoryId,
        productCategoryName: payload.categoryName,
        enableFlg: payload.enableFlg,
      },
    })
    resultModal.show('カテゴリ割当を保存しました。')
    categoryEditTarget.value = null
  } catch {
    resultModal.show('カテゴリ割当の保存に失敗しました。', 'ng')
  }
}

async function onDeleteCategory() {
  const t = categoryEditTarget.value
  const asin = selection.selectedAsin
  if (!t || !asin || t.scope !== 'asin') return
  if (!(await confirmModal.ask('このASINのカテゴリ上書きを削除しますか？'))) return
  try {
    await saveCategoryMutation.mutateAsync({
      asin,
      req: { scope: 'asin', dbName: t.dbName, action: 'delete' },
    })
    resultModal.show('カテゴリ上書きを削除しました。')
    categoryEditTarget.value = null
  } catch {
    resultModal.show('削除に失敗しました。', 'ng')
  }
}
</script>

<template>
  <div class="card">
    <!-- タイトル・折りたたみは不要のため出さない。常に展開表示する -->
    <!-- ブラウズノードの追加・削除・兄弟反映を行うツールバー -->
    <div v-if="selection.selectedAsin" class="cat-edit-bar">
      <button class="btn btn-ghost btn-sm" @click="showPicker = true" :disabled="manageMutation.isPending.value">
        <svg class="icon icon-xs" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        ブラウズノード追加
      </button>
      <button
        class="btn btn-ghost btn-sm"
        @click="shareToSiblings"
        :disabled="manageMutation.isPending.value || browseNodes.length === 0"
        title="現在のブラウズノード構成を兄弟商品にコピー"
      >
        <svg class="icon icon-xs" viewBox="0 0 24 24"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
        兄弟へ反映
      </button>
      <span v-if="manageMutation.isPending.value" class="cat-edit-busy">更新中…</span>
    </div>

    <div v-if="isLoading" class="empty-state" style="padding:14px 0;"><span class="spinner"></span> 読み込み中…</div>

    <!-- ブラウズノードが1件も無い(VB版は背景を赤にする) -->
    <div v-else-if="!hasBrowseNode" class="cat-empty-danger">
      ブラウズノードが割り当てられていません
      <button class="btn btn-ghost btn-sm" style="margin-top:8px;" @click="showPicker = true">
        ブラウズノードを追加
      </button>
    </div>

    <template v-else>
      <div v-for="node in browseNodes" :key="node.browseNodeId" class="cat-node">
        <!-- ブラウズノード本体(無効なら警告色) -->
        <div class="cat-node-row" :class="{ warn: !node.enabled }">
          <span class="cat-mall-icon amazon">a</span>
          <span class="cat-id chip-id">{{ node.browseNodeId }}</span>
          <span class="cat-route" :title="node.browseNodeRouteName">{{ node.browseNodeRouteName || node.browseNodeName || '-' }}</span>
          <span v-if="!node.enabled" class="cat-flag-ng" title="このブラウズノードは無効です">⚠</span>
          <!-- 削除ボタン -->
          <button
            class="cat-node-del"
            title="このブラウズノードを削除"
            :disabled="manageMutation.isPending.value"
            @click="deleteNode(node.browseNodeId)"
          >
            <svg class="icon icon-xs" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- モール毎のカテゴリ -->
        <div class="cat-malls">
          <div
            v-for="mall in node.mallCategories"
            :key="mall.mallCode"
            class="cat-mall-row"
            :class="{ warn: !mall.enabled, danger: mall.danger }"
          >
            <img v-if="mall.iconUrl" class="cat-mall-icon-img" :src="mall.iconUrl" :alt="mall.mallName" />
            <span v-else class="cat-mall-icon">{{ mall.mallName.slice(0, 1) }}</span>

            <span class="cat-id chip-id" :class="{ fork: mall.fixedItem }">{{ mall.categoryId || '-' }}</span>

            <span
              class="cat-name"
              :class="{ fork: mall.fixedItem }"
              :title="mall.fixedItem ? fixedItemTooltip(mall) : mall.categoryName"
            >
              {{ mall.categoryName || (mall.danger ? '（カテゴリ未割当）' : '-') }}
            </span>

            <span v-if="mall.source === 'asin_override'" class="cat-badge override" title="ASIN単位で上書きされています">ASIN優先</span>
            <span v-if="mall.fixedItem" class="cat-badge fork" :title="fixedItemTooltip(mall)">モール補正</span>
            <span v-if="!mall.enabled" class="cat-flag-ng" title="このカテゴリは無効です">⚠</span>
            <span v-if="mall.error" class="cat-badge err" :title="mall.error">取得失敗</span>

            <!-- カテゴリ編集ボタン(CM2=ブラウズノード単位 / CM3=ASIN単位) -->
            <span class="cat-mall-edit">
              <button
                class="cat-cat-edit"
                title="ブラウズノード単位でカテゴリを割当(CategoryMapper2相当)"
                @click="editBrowsenodeCategory(node, mall)"
              >BN</button>
              <button
                class="cat-cat-edit asin"
                title="このASIN単位でカテゴリを割当(CategoryMapper3相当)"
                @click="editAsinCategory(mall)"
              >ASIN</button>
            </span>
          </div>
        </div>
      </div>

    </template>

    <!-- ブラウズノード追加モーダル。
         ブラウズノードが0件のとき(「割り当てられていません」の表示中)にも開けるよう、
         一覧の表示(v-else の template)の外に置く。以前は中にあったため、0件のときは
         「ブラウズノード追加」「ブラウズノードを追加」を押しても何も起きなかった。 -->
    <BrowseNodePickerModal
      v-if="showPicker && selection.selectedAsin"
      :asin="selection.selectedAsin"
      :product-name="productName"
      :assigned-ids="assignedIds"
      @close="showPicker = false"
      @select="onSelectBrowseNode"
    />

    <!-- モールカテゴリ編集モーダル(CM2/CM3) -->

    <MallCategoryPickerModal
      v-if="categoryEditTarget"
      :mall-name="categoryEditTarget.mallName"
      :db-name="categoryEditTarget.dbName"
      :scope="categoryEditTarget.scope"
      :target-label="categoryEditTarget.targetLabel"
      :current-category-id="categoryEditTarget.currentCategoryId"
      :current-enable-flg="categoryEditTarget.currentEnableFlg"
      :deletable="categoryEditTarget.deletable"
      @close="categoryEditTarget = null"
      @save="onSaveCategory"
      @delete="onDeleteCategory"
    />
  </div>
</template>
